/**
 * #2022 Phase 3 — per-project order rules (client-side, hard-enforced).
 *
 * Pure helpers shared by the unified product-capture screen. The server
 * (orders/apis/view.py) remains the authority — these mirror its checks so a
 * rep can never build an order the server would 400 (which today surfaces as a
 * silent "Order saved" failure).
 *
 * Rules:
 *  - order_increment: order quantities move in multiples of N (per product;
 *    default 1 → today's behavior).
 *  - MinOrderAmount: the order total (sum of orderqty*cost) must be >= the
 *    distributor minimum.
 *  - MaxProductCount: each line's orderqty must be <= the distributor cap
 *    (matches the server's per-line check).
 *
 * Order rules apply ONLY when the project requires an order. An inventory-only
 * submission (orderqty=0 everywhere) is never blocked by them.
 */

export interface OrderLine {
  productid: number;
  orderqty: number;
  cost: number;
  /** Multiple the order quantity must be a multiple of (default 1). */
  order_increment?: number | null;
}

export interface OrderRuleContext {
  requiresOrder: boolean;
  minOrderAmount?: number | null;
  maxProductCount?: number | null;
}

export interface OrderValidation {
  ok: boolean;
  /** Sum of orderqty*cost across all lines. */
  total: number;
  /** Human-readable reasons the order is blocked (empty when ok). */
  reasons: string[];
  /** True when an order is required but the total is below MinOrderAmount. */
  underMin: boolean;
  /** Product ids whose orderqty exceeds MaxProductCount. */
  overMaxProductIds: number[];
  /** Product ids whose orderqty isn't a multiple of the product's order_increment. */
  badIncrementProductIds: number[];
}

/** Normalize an increment to a positive integer step (missing/<=0 → 1). */
function normIncrement(increment?: number | null): number {
  return increment && increment > 0 ? increment : 1;
}

/** Snap a quantity to the nearest multiple of `increment`, clamped to >= 0. */
export function snapToIncrement(qty: number, increment?: number | null): number {
  const inc = normIncrement(increment);
  if (qty <= 0) return 0;
  return Math.max(0, Math.round(qty / inc) * inc);
}

/** Step an aligned quantity up/down by one increment, clamped to >= 0. */
export function stepQty(qty: number, direction: 1 | -1, increment?: number | null): number {
  const inc = normIncrement(increment);
  const base = snapToIncrement(qty, inc);
  return Math.max(0, base + direction * inc);
}

/** Validate the current order lines against the project's distributor rules. */
export function validateOrder(lines: OrderLine[], ctx: OrderRuleContext): OrderValidation {
  const total = lines.reduce((sum, l) => sum + l.orderqty * l.cost, 0);

  // Inventory-only (or otherwise no-order) projects: order rules don't apply.
  if (!ctx.requiresOrder) {
    return { ok: true, total, reasons: [], underMin: false, overMaxProductIds: [], badIncrementProductIds: [] };
  }

  const reasons: string[] = [];

  // MaxProductCount: a falsy cap (null OR 0) means "no cap" — matches the
  // server, which does `if not maxproductcount: continue`.
  const hasCap = ctx.maxProductCount != null && ctx.maxProductCount > 0;
  const overMaxProductIds = hasCap
    ? lines.filter((l) => l.orderqty > ctx.maxProductCount!).map((l) => l.productid)
    : [];
  if (overMaxProductIds.length > 0) {
    reasons.push(`Some lines exceed the maximum product count (${ctx.maxProductCount}).`);
  }

  // order_increment: a positive order qty must be a multiple of the product's
  // increment (mirrors the server's modulo check). Zero-qty lines (inventory/
  // OOS) are exempt.
  const badIncrementProductIds = lines
    .filter((l) => {
      const inc = l.order_increment && l.order_increment > 1 ? l.order_increment : 1;
      return inc > 1 && l.orderqty > 0 && l.orderqty % inc !== 0;
    })
    .map((l) => l.productid);
  if (badIncrementProductIds.length > 0) {
    reasons.push('Some order quantities are not in the required multiples.');
  }

  const underMin = ctx.minOrderAmount != null && total < ctx.minOrderAmount;
  if (underMin) {
    reasons.push(`Order total is below the minimum order amount ($${ctx.minOrderAmount}).`);
  }

  return { ok: reasons.length === 0, total, reasons, underMin, overMaxProductIds, badIncrementProductIds };
}

/**
 * #2188 (diag #54) — detect the "project requires an order but has no
 * distributor linked" failure. The by-project products endpoint returns a hard
 * 400 with "Project does not have a retailer or distributor". This is a setup
 * problem (not transient/scan), so OrderEntry shows a clear message and steers
 * the rep away from the generic catalog. Duck-typed so it works for ApiError
 * (which carries `status` + the extracted backend message) without importing it.
 */
export function isOrderingNotConfiguredError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const { status, message } = err as { status?: number; message?: string };
  return status === 400 && typeof message === 'string' && /retailer or distributor/i.test(message);
}
